import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ImportFileUpload from './ImportFileUpload';
import ImportFileError from './ImportFileError';

const VIEWS = {
    FILE: 'file',
    ERROR: 'error',
};

const ERROR_MAPPING = 'import_requires_mapping';

const STEPS = {
    UPLOADING: 'uploading',
    PROCESSING: 'processing',
    SUGGESTING: 'suggesting',
};

export default function ImportFileCSV(props) {
    const {
        onClose,
        onSuccess,
        onMapping,
        onChangeProcessing,
        processing,
        importService,
        suggestService,
        children,
    } = props;
    const [view, setView] = useState(VIEWS.FILE);
    const [progressStep, setProgressStep] = useState(null);
    const [errorType, setErrorType] = useState(null);
    const [errorInfo, setErrorInfo] = useState(null);

    const { t } = useTranslation('common');

    const stepTexts = {
        [STEPS.UPLOADING]: t('import-step-uploading'),
        [STEPS.PROCESSING]: t('import-step-processing'),
        [STEPS.SUGGESTING]: t('import-step-suggesting'),
    };

    const importAndSuggest = async (file) => {
        setProgressStep(STEPS.UPLOADING);

        const response = await importService(file, setProgressStep);

        if (response.error === ERROR_MAPPING && suggestService) {
            setProgressStep(STEPS.SUGGESTING);

            try {
                response.suggestion = await suggestService(response.importId);
            } catch {
                // The suggestion is best-effort: on failure, fall back to manual mapping
                response.suggestion = null;
            }
        }

        return response;
    };

    return (
        <>
            {view === VIEWS.ERROR && <ImportFileError onClose={onClose} type={errorType} info={errorInfo} />}

            {view === VIEWS.FILE && (
                <ImportFileUpload
                    onClose={onClose}
                    processing={processing}
                    onChangeProcessing={onChangeProcessing}
                    importService={importAndSuggest}
                    busyContent={progressStep ? stepTexts[progressStep] : null}
                    onSubmit={({ importId, columns, suggestion, error, errorInfo }) => {
                        setProgressStep(null);

                        if (!error) {
                            onSuccess();
                        } else if (error === ERROR_MAPPING) {
                            onMapping({ importId, columns, suggestion: suggestion ?? null });
                        } else {
                            setErrorType(error);
                            setErrorInfo(errorInfo);
                            setView(VIEWS.ERROR);
                        }
                    }}
                >
                    {children}
                </ImportFileUpload>
            )}
        </>
    );
}

ImportFileCSV.propTypes = {
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    onMapping: PropTypes.func.isRequired,
    processing: PropTypes.bool.isRequired,
    onChangeProcessing: PropTypes.func.isRequired,
    importService: PropTypes.func.isRequired,
    suggestService: PropTypes.func,
    children: PropTypes.node,
};
